import { z } from 'zod';
import type { AppChat, FinalidadeEstado, PerfilPadrao, PorteEmpresa, TipoHabilitacao } from '../enums/dominios';
import { validarCnpj, validarCpf } from '../utils/documentos';
import type { SituacaoValidade } from '../utils/documentos';

// Configurações da empresa e do sistema (ADR-0022). Só uuids trafegam; o papel na empresa decide as seções.

export type SecaoConfiguracao = 'empresa' | 'documentos' | 'usuarios' | 'espacos' | 'estados' | 'padroes' | 'envios' | 'orcamento-ia';
export type PapelEmpresa = 'administrador' | 'gestor' | 'membro';
export type AcessoConfiguracoesDto = { papel: PapelEmpresa; secoes: SecaoConfiguracao[]; superadministrador: boolean };

const texto = (max: number) => z.string().trim().max(max).nullable().optional();
const digitos = (n: number) => z.string().regex(new RegExp(`^\\d{${n}}$`)).nullable().optional();
const data = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional();

export const esquemaEmpresa = z.object({
  versao: z.number().int().positive(),
  nome: z.string().trim().min(2).max(200),
  razaoSocial: texto(200),
  cnpj: z.string().trim().refine(validarCnpj, 'CNPJ inválido.').nullable().optional(),
  inscricaoEstadual: texto(30),
  inscricaoMunicipal: texto(30),
  porte: z.number().int().min(1).max(3).nullable().optional(),
  cep: digitos(8),
  logradouro: texto(200),
  numero: texto(20),
  complemento: texto(100),
  bairro: texto(100),
  municipio: texto(120),
  uf: z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/).nullable().optional(),
  telefone: texto(20),
  email: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  site: texto(300),
  representanteNome: texto(200),
  representanteCpf: z.string().regex(/^\d{11}$/).refine(validarCpf, 'CPF inválido.').nullable().optional(),
  representanteCargo: texto(100),
  representanteEmail: z.string().trim().toLowerCase().email().max(254).nullable().optional(),
  banco: texto(100),
  agencia: texto(20),
  conta: texto(30),
});
export type EmpresaEntrada = z.infer<typeof esquemaEmpresa>;

type SemVersao = Omit<EmpresaEntrada, 'versao' | 'porte'>;
export type EmpresaConfigDto = { [K in keyof SemVersao]-?: string | null } & {
  uuid: string;
  versao: number;
  nome: string;
  porte: PorteEmpresa | null;
  logoUrl: string | null;
};

export type DocumentoEmpresaDto = {
  uuid: string;
  nome: string;
  tipo: TipoHabilitacao;
  emitidoEm: string | null;
  validoAte: string | null;
  situacao: SituacaoValidade;
  observacao: string | null;
  arquivo: { nome: string; tamanhoBytes: number; mime: string } | null;
  enviadoEm: string;
};
export type DocumentosEmpresaDto = { documentos: DocumentoEmpresaDto[]; diasAviso: number; podeEditar: boolean };

export const esquemaDocumentoEmpresa = z.object({
  nome: z.string().trim().min(2).max(255),
  tipo: z.coerce.number().int().min(1).max(14),
  emitidoEm: data,
  validoAte: data,
  observacao: texto(500),
});
export type DocumentoEmpresaEntrada = z.infer<typeof esquemaDocumentoEmpresa>;

export type QuadroDoUsuarioDto = { quadroUuid: string; quadro: string; perfil: string; perfilPadrao: PerfilPadrao | null; superior: string | null };
export type UsuarioEmpresaDto = {
  uuid: string;
  nome: string;
  email: string;
  avatarUrl: string | null;
  administrador: boolean;
  ativo: boolean;
  bloqueio: { em: string; por: string | null; motivo: string | null } | null;
  ultimoAcesso: string | null;
  quadros: QuadroDoUsuarioDto[];
};
export type UsuariosEmpresaDto = { usuarios: UsuarioEmpresaDto[]; podeBloquear: boolean };

export const esquemaMotivo = z.object({ motivo: z.string().trim().min(3).max(500) });

export type QuadroConfigDto = { uuid: string; nome: string; descricao: string | null; membros: number; processos: number; criadoEm: string };
export const esquemaQuadroConfig = z.object({ nome: z.string().trim().min(2).max(120), descricao: texto(500) });

export type EstadoConfigDto = {
  uuid: string;
  nome: string;
  finalidade: FinalidadeEstado;
  ordem: number;
  cor: string;
  icone: string;
  exigeMotivo: boolean;
  /** Novo, Sem estado, Arquivo e Lixeira: renomeiam, não saem. */
  fixo: boolean;
  processos: number;
  motivos: string[];
};
const cor = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
export const esquemaEstadoConfig = z.object({ nome: z.string().trim().min(1).max(60), cor, exigeMotivo: z.boolean(), motivos: z.array(z.string().trim().min(2).max(200)).max(30).optional() });
export const esquemaNovoEstado = z.object({ nome: z.string().trim().min(1).max(60), cor });
export const esquemaOrdemEstados = z.object({ uuids: z.array(z.string().uuid()).min(1).max(50) });

export type PadroesDto = {
  versao: number;
  limiteArquivoMb: number;
  diasPrazoCritico: number;
  diasAvisoValidade: number;
  orcamentoIaMensal: number | null;
  minutosInatividade: number;
  appChat: AppChat | null;
};
export const esquemaPadroes = z.object({
  versao: z.number().int().positive(),
  limiteArquivoMb: z.number().int().min(1).max(500),
  diasPrazoCritico: z.number().int().min(0).max(30),
  diasAvisoValidade: z.number().int().min(1).max(180),
  orcamentoIaMensal: z.number().min(0).max(1000000).nullable(),
  minutosInatividade: z.number().int().min(5).max(480),
  appChat: z.number().int().min(1).max(4).nullable(),
});
